/**
 * ApiClientContext — constructs the singleton ApiClient for the admin panel.
 *
 * Provider order (main.tsx): MsalProvider -> AuthProvider -> ApiClientProvider
 *
 * AuthProvider runs ABOVE this; it constructs its own one-shot api-client
 * inside bootstrap() for the `users.byEmail(upn)` lookup, then we read the
 * resolved officerCode here and build the full-context client that every
 * editor uses for /v3/admin/* writes.
 *
 * Why this matters: the AdminAuthService path that actually accepts writes
 * today is Path C — `x-pcb-officer-code` matching a row in
 * UserRoleAssignments (admin.auth.allowBypassKey is OFF in prod). The
 * api-client http transport attaches that header automatically when
 * `context().officerCode` is set, so a resolved user is a prerequisite.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiClient, type ApiClientConfig } from '@retail-ops/api-client';
import { getRetailToken } from '@/auth/tokens';
import { useAuth } from '@/contexts/AuthContext';

const CLIENT_ID = 'admin-panel';
const CLIENT_VERSION = 'admin-panel@0.8.0';

function resolveBaseUrl(): string {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error('[admin-panel] VITE_API_BASE_URL is not set.');
  }
  return baseUrl || 'https://pcb-retail-api.azurewebsites.net/api';
}

function resolveFunctionKey(): string {
  return (import.meta.env.VITE_FUNCTION_KEY as string | undefined) ?? '';
}

const ApiClientContext = createContext<ApiClient | null>(null);

export function ApiClientProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const officerCode = user?.officerCode;
  const branchCode = user?.branchCode;
  const regionCode = user?.regionCode;
  const userRole = user?.roles?.[0];

  const client = useMemo<ApiClient>(() => {
    const cfg: ApiClientConfig = {
      baseUrl: resolveBaseUrl(),
      functionKey: resolveFunctionKey(),
      version: CLIENT_VERSION,
      getToken: async () => {
        try {
          // Shared backend token when BT has granted consent, else the ID token (auth/tokens.ts).
          return await getRetailToken();
        } catch {
          return undefined;
        }
      },
      context: () => {
        const ctx: {
          clientId: string;
          officerCode?: string;
          branchCode?: string;
          regionCode?: string;
          userRole?: string;
        } = { clientId: CLIENT_ID };
        if (officerCode) ctx.officerCode = officerCode;
        if (branchCode) ctx.branchCode = branchCode;
        if (regionCode) ctx.regionCode = regionCode;
        if (userRole) ctx.userRole = userRole;
        return ctx;
      },
    };
    return new ApiClient(cfg);
  }, [officerCode, branchCode, regionCode, userRole]);

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error('useApiClient must be used within ApiClientProvider');
  return ctx;
}
